import { Activity } from '@cube3/common/model/schema/resources/activities';
import {
  useActivityContext,
  useActivityContextWithPath
} from './useActivityContext';
import { useActivityActor } from './useActivityActor';
import { useActivitySubject } from './useActivitySubject';
import {
  getActivityImage,
  getActivityImageType
} from '../utils/getActivityImage';
import { useMemo } from 'react';
import {
  getChangedValue,
  getPattern,
  getPrep,
  getVerb
} from '../utils/getActivityConfig';
import { useActivityClickBehavior } from './useActivityClickBehavior';

export const useWorkspaceActivityFeed = (
  activity: Activity,
  onClose?: () => void
) => {
  const { action, created_at: date } = activity;
  const { subject, actor, related_resources: context } = activity.relationships;

  const { resource: contextResource } = useActivityContext(context);

  const { name: actorName, image: actorAvatar } = useActivityActor(actor);
  const {
    name: subjectName,
    resource: subjectResource,
    shareIntent
  } = useActivitySubject(subject, actor?.id);

  const sourceImage = getActivityImage({
    resource: subjectResource || subject,
    context: contextResource || {},
    action: action
  });

  const { type: contextType, name: contextName } =
    useActivityContextWithPath(activity);

  const { visitObject, visitContext } = useActivityClickBehavior({
    activity,
    onClose
  });

  //#region Generate message

  const imageSection = useMemo(() => {
    const type = getActivityImageType(action, 'workspace');
    return {
      type: type,
      image: type === 'avatar' ? actorAvatar : sourceImage
    };
  }, [action, actorAvatar, sourceImage]);

  const { topRowSection, bottomRowSection } = useMemo(() => {
    let topRowSection = [];
    let bottomRowSection = [];

    const what = {
      name: activity?.['oldValue'] || subjectName,
      href: undefined,
      onClick: visitObject(true)
    };
    const who = {
      name: actorName
    };
    const where = contextName && {
      name: contextName,
      icon: contextType,
      herf: undefined,
      onClick: visitContext
    };
    const verb = getVerb(activity, shareIntent);
    const prep = getPrep(action);
    // {What} was did by {who} at {time} in {where}
    if (getPattern(action, 'workspace') === 'passive') {
      if (action.toLowerCase().includes('updated')) {
        const { newVal, oldVal } = getChangedValue(activity);
        topRowSection = [
          {
            ...what,
            name: oldVal,
            maxWidth: 60
          },
          ' was changed to',
          {
            ...what,
            name: newVal,
            maxWidth: 60
          },
          ' by',
          who
        ];
      } else {
        topRowSection = [
          {
            ...what,
            maxWidth: 120
          },
          ' was ' + verb,
          ' by',
          who
        ];
      }
    }
    // {Who} did {what}, at {time} in {where}
    else {
      topRowSection = [{ ...who, maxWidth: 120 }, verb, what];
    }
    bottomRowSection = [date, where && prep, where];
    return { topRowSection, bottomRowSection };
  }, [
    activity,
    contextType,
    subjectName,
    contextName,
    visitObject,
    visitContext
  ]);

  return { imageSection, topRowSection, bottomRowSection };
};
